import React, { useId, useMemo } from 'react';
import { Table, Button, Pagination, Spinner, Form } from 'react-bootstrap';
import { useSubsidiaries, useOrders } from '../hooks';
import { SubsidiaryModal } from './Orders/Modals';
import './Orders.css';

const Orders = ({ statusId, title }) => {
    const formControlId = useId();
    const {
        currentPage,
        filterSub,
        loading,
        orders,
        modals,
        shipType,
        sortOrder,
        totalPages,
        orderIdSubsidiary,
        updating,
        shippingTypes,
        extractScheduledDate,
        formatCurrency,
        getStatusOptions,
        openOrderSubsidiaryModal,
        closeOrderSubsidiaryModal,
        handleCurrentPage,
        handleOrderClick,
        handleRefresh,
        handleShipTypeChange,
        handleFilterSubChange,
        handleSortById,
        handleSortByScheduledDate,
        handleStatusChange,
        isToday,
    } = useOrders(statusId, title);

    const { subsidiaries } = useSubsidiaries();

    // Memoized order rows for performance
    const orderRows = useMemo(() => {
        if (!Array.isArray(orders) || orders.length === 0) {
            return (
                <tr>
                    <td colSpan="10" className="text-center">
                        No hay órdenes en este estado.
                    </td>
                </tr>
            );
        }

        return orders.map((order) => {
            const scheduledDate = extractScheduledDate(order.customer_message);
            const isScheduledForToday = isToday(scheduledDate);
            const shippingMethod =
                order.consignments.shipping[0]?.shipping_method || '';
            const hasSubsidiaryInMethod = shippingMethod.includes('Sucursal');
            const showSubsidiaryModalBtn =
                statusId === 1 && !hasSubsidiaryInMethod;

            return (
                <tr key={order.id}>
                    <td>{order.id}</td>
                    <td>{order.customer_first_name || 'Desconocido'}</td>
                    <td>{order.billing_address.last_name}</td>
                    <td>{`${order.billing_address.first_name} ${order.billing_address.last_name}`}</td>
                    <td>{formatCurrency(order.total_inc_tax)}</td>
                    <td>
                        {order.status_id === 9 || order.status_id === 10 ? (
                            <span>{order.custom_status || order.status}</span>
                        ) : (
                            <select
                                value={order.custom_status || order.status}
                                onChange={(e) =>
                                    handleStatusChange(order.id, e.target.value)
                                }
                            >
                                {getStatusOptions(order).map((option) => (
                                    <option
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        )}
                    </td>
                    <td
                        style={{
                            backgroundColor: isScheduledForToday
                                ? '#ffc10773'
                                : 'transparent',
                        }}
                    >
                        {scheduledDate}
                    </td>
                    <td>
                        {!hasSubsidiaryInMethod
                            ? order.subsidiary?.name || 'No asignado'
                            : 'No aplica'}
                    </td>
                    <td>
                        {order.shipping_method ? (
                            order.shipping_method
                        ) : (
                            <Spinner animation="border" size="sm" />
                        )}
                    </td>
                    <td>
                        <Button
                            variant="primary"
                            onClick={() => handleOrderClick(order.id)}
                        >
                            Detalle
                        </Button>
                        {showSubsidiaryModalBtn && (
                            <Button
                                style={{ marginTop: '0.5rem' }}
                                variant="primary"
                                title={
                                    order.subsidiary
                                        ? 'Cambiar sucursal'
                                        : 'Asignar sucursal'
                                }
                                onClick={() =>
                                    openOrderSubsidiaryModal(order.id)
                                }
                            >
                                {order.subsidiary ? 'Cambiar' : 'Asignar'}
                            </Button>
                        )}
                    </td>
                </tr>
            );
        });
    }, [
        orders,
        statusId,
        extractScheduledDate,
        isToday,
        formatCurrency,
        getStatusOptions,
        handleStatusChange,
        handleOrderClick,
        openOrderSubsidiaryModal,
    ]);

    // Memoized pagination items
    const paginationItems = useMemo(() => {
        const items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = startPage + maxVisible - 1;

        if (endPage > totalPages) {
            endPage = totalPages;
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
        }

        for (let page = startPage; page <= endPage; page++) {
            items.push(
                <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handleCurrentPage(page)}
                >
                    {page}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
        }

        return items;
    }, [currentPage, totalPages, handleCurrentPage]);

    return (
        <div className="container mt-4">
            {(loading || updating) && (
                <div className="overlay">
                    <Spinner animation="border" role="status">
                        <span className="sr-only"></span>
                    </Spinner>
                </div>
            )}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h1 className="title">{title}</h1>
                <div className="d-flex justify-content-between align-items-end content-buttons">
                    <Button
                        variant={sortOrder === 'id' ? 'warning' : 'light'}
                        onClick={handleSortById}
                        className="btn-secondary mx-2px"
                    >
                        Ordenar por ID
                    </Button>
                    <Button
                        variant={
                            sortOrder === 'scheduled_date' ? 'warning' : 'light'
                        }
                        onClick={handleSortByScheduledDate}
                        className="btn-secondary mx-2px"
                    >
                        Ordenar por Fecha Programada
                    </Button>
                    <Form.Group className="mx-2px">
                        <Form.Label
                            htmlFor={formControlId + '-orderByShipping'}
                        >
                            Ordenar por tipo de envío
                        </Form.Label>
                        <Form.Select
                            id={formControlId + '-orderByShipping'}
                            aria-describedby={
                                formControlId + '-orderByShipping'
                            }
                            onChange={({ target }) =>
                                handleShipTypeChange(target.value)
                            }
                            value={shipType}
                        >
                            <option value="">Todos los envíos</option>
                            {shippingTypes?.map((type, i) => (
                                <option
                                    key={`${formControlId}-${i}-${type}`}
                                    value={type}
                                >
                                    {type}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mx-2px">
                        <Form.Label htmlFor={formControlId + '-filterBySub'}>
                            Sucursal Asignado
                        </Form.Label>
                        <Form.Select
                            id={formControlId + '-filterBySub'}
                            aria-describedby={formControlId + '-filterBySub'}
                            onChange={({ target }) =>
                                handleFilterSubChange(target.value)
                            }
                            value={filterSub}
                        >
                            <option value="">Sin filtro</option>
                            <option value="N/A">No aplica</option>
                            <option value="Unassigned">No asignado</option>
                            {subsidiaries?.map(({ id, name }, i) => (
                                <option
                                    key={`${formControlId}-${i}-${id}`}
                                    value={id}
                                >
                                    {name}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Button
                        variant="primary"
                        onClick={handleRefresh}
                        className="btn-primary mx-2px"
                    >
                        Actualizar
                    </Button>
                </div>
            </div>
            <div className="table-container">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Orden ID</th>
                            <th>Nombre</th>
                            <th>Cliente Beth Yosef</th>
                            <th>Nombre de facturación</th>
                            <th>Total</th>
                            <th>Status</th>
                            <th>Fecha programada de envío</th>
                            <th>Sucursal Asignado</th>
                            <th>Tipo de envío</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>{orderRows}</tbody>
                </Table>
            </div>
            {statusId === 1 && (
                <SubsidiaryModal
                    refresh={handleRefresh}
                    show={modals.subsidiary}
                    onHide={closeOrderSubsidiaryModal}
                    orderId={orderIdSubsidiary}
                />
            )}
            <Pagination>
                <Pagination.First
                    onClick={() => handleCurrentPage(1)}
                    disabled={currentPage === 1}
                    style={
                        currentPage === 1
                            ? { opacity: 0.5, cursor: 'not-allowed' }
                            : {}
                    }
                    title={
                        currentPage === 1
                            ? 'Ya está en la primera página'
                            : 'Ir a la primera página'
                    }
                />
                <Pagination.Prev
                    onClick={() => handleCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                />
                {paginationItems}
                <Pagination.Next
                    onClick={() => handleCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                />
                <Pagination.Last
                    onClick={() => handleCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                />
            </Pagination>
        </div>
    );
};

export default Orders;
